import { createAsyncThunk, createSlice, createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import {
  Conversation,
  fetchConversationsWithMessages,
  fetchConversationWithMessages,
  sendMessage,
  updateConversationStatus,
  fetchConversationsFullData,
  deleteConversations,
} from './conversations.slice';
import { serializeErrorMessage } from '../../utils';
import { httpClient } from 'src/libs';

export const priorityStatuses = ['needs_attention', 'error'];

// Define a type for the filter object to ensure type safety
export interface FilterObject {
  status?: string;
  limit?: number;
  messagesLimit?: number;
  has_draft_messages?: boolean;
  has_engaged_users?: boolean;
  has_scheduled_delivery?: boolean;
  delivery_state?: string;
  autocomplete_status?: string;
  contactId?: string;
  subjectContains?: string;
  queryString?: string;
  universal_search?: string;
  user_not_responded_to?: boolean;
  memory_contains?: any;
  channel_id?: string;
  cursor?: string | null;
  conversationLatestDeliveryAtBefore?: Date | null;
  conversationLatestMessageCreatedAtBefore?: Date | null;
  conversationLastUpdatedBefore?: Date | null;
  starred?: boolean;
  full_payload?: boolean;
  [key: string]: any; // Allow additional properties for flexibility
}

interface FilteredConversationsState extends EntityState<Conversation> {
  conversationsLoading: boolean;
  conversationsPresent: boolean;
  error: string | null;
  filterObject: FilterObject;
  conversationsUpdatedSinceFilterSet: boolean;
  isFilterActive: boolean;
  totalConversations: number;
  hasMore: boolean;
  hasMorePriority: boolean;
  lastCursor: string | null;
  limit: number;
  priorityConversations: Conversation[];
  priorityConversationsLoading: boolean;
}

// Create entity adapter for conversations
const filteredConversationsAdapter = createEntityAdapter<Conversation>({
  selectId: (conversation) => conversation.id,
  sortComparer: (a, b) => {
    // Safely compare the latest activity timestamps of two conversations.
    // We convert the date strings (if present) to numeric timestamps so that
    // we can perform simple arithmetic subtraction. Any missing dates will
    // be treated as 0 (i.e. older than any real timestamp).
    const getTimestamp = (conversation: Conversation): number => {
      const dateString = conversation.latest_delivery_at || conversation.latest_message_at || conversation.updated_at;
      return dateString ? new Date(dateString).getTime() : 0;
    };

    return getTimestamp(b) - getTimestamp(a);
  },
});

// Initial state using the adapter's getInitialState method
const initialState: FilteredConversationsState = filteredConversationsAdapter.getInitialState({
  conversationsLoading: false,
  conversationsPresent: false,
  error: null,
  filterObject: {},
  conversationsUpdatedSinceFilterSet: false,
  isFilterActive: false,
  totalConversations: 0,
  hasMore: false,
  hasMorePriority: false,
  lastCursor: null,
  limit: 15,
  priorityConversations: [],
  priorityConversationsLoading: false,
});

// Async thunk to fetch filtered conversations
export const fetchFilteredConversations = createAsyncThunk(
  'filteredConversations/fetch',
  async (queryParams: any, thunkAPI) => {
    try {
      const apiParams: any = { ...queryParams };

      // Use unwrap() so we directly get the payload instead of the dispatched action object
      const response: any = await thunkAPI
        .dispatch(fetchConversationsWithMessages(apiParams) as any)
        .unwrap();

      const conversations = response?.conversations;

      if (!Array.isArray(conversations)) {
        return thunkAPI.rejectWithValue('Failed to fetch conversations');
      }

      // If we are explicitly requesting priority conversations, tag them so downstream utils can recognise them
      if (apiParams.is_priority) {
        return conversations.map((conv: any) => ({ ...conv, is_priority: true }));
      }

      return conversations;
    } catch (error) {
      // Check if the error is due to request cancellation
      if (error.name === 'AbortError') {
        return thunkAPI.rejectWithValue('Request cancelled');
      }
      const message = serializeErrorMessage(error);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchPriorityConversations = createAsyncThunk(
  'filteredConversations/fetchPriority',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch priority status conversations
      const statusQueryParams = new URLSearchParams({
        limit: '50',
        messages_limit: '3',
        is_priority: 'true',
        full_payload: 'false'
      }).toString();

      const statusResponse = await httpClient.get(`${process.env.REACT_APP_BASE_URL}/comms-api/conversations/?${statusQueryParams}`);

      if (!statusResponse.data?.success) {
        return rejectWithValue('Failed to fetch priority conversations');
      }

      const statusConversations = statusResponse.data || [];

      return statusConversations;
    } catch (error) {
      const message = serializeErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

const filteredConversationsSlice = createSlice({
  name: 'filteredConversations',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      const payload = action.payload ?? {};

      // Ensure we always have an object stored
      state.filterObject = payload as any;

      // Update limit if provided in the filter payload and it is a finite number
      if (payload && typeof payload === 'object' && payload.limit !== undefined) {
        state.limit = payload.limit;
      }

      // Determine if the filter is active by checking all filter properties
      const filterObject: any = payload;
      const booleanFilters = [
        'has_draft_messages',
        'has_engaged_users',
        'has_scheduled_delivery',
        'user_not_responded_to'
      ];

      // Check if any filter value is non-empty
      state.isFilterActive = false;

      // First check boolean filters explicitly
      for (const key of booleanFilters) {
        if (filterObject[key] === true) {
          state.isFilterActive = true;
          break;
        }
      }

      // Then check other filter properties
      if (!state.isFilterActive) {
        const values = Object.entries(filterObject || {});
        state.isFilterActive = values.some(([key, value]) => {
          // Skip boolean filters as we already checked them
          if (booleanFilters.includes(key)) return false;

          // Check array values
          if (Array.isArray(value)) return value.length > 0;

          // Check string, number, or other values
          return value !== '' && value != null && value !== false;
        });
      }

      state.conversationsUpdatedSinceFilterSet = false;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchFilteredConversations actions
    builder
      .addCase(fetchFilteredConversations.pending, (state) => {
        state.conversationsLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredConversations.fulfilled, (state, action) => {
        // Check if this is a cursor-based pagination request (loading more)
        const hasCursor = action.meta.arg && action.meta.arg.cursor;
        
        if (hasCursor) {
          // For cursor-based pagination, append new conversations
          filteredConversationsAdapter.addMany(state, action.payload);
        } else {
          // For initial load or filter changes, replace all conversations
          filteredConversationsAdapter.setAll(state, action.payload);
        }
        
        state.conversationsLoading = false;
        state.conversationsPresent = state.ids.length > 0;
        state.error = null;
        state.conversationsUpdatedSinceFilterSet = false;

        // When fetching filtered conversations, also update pagination state
        if (action.meta.arg && action.meta.arg.is_priority) {
          // For priority conversations
          state.hasMorePriority = !!action.meta.arg.has_more;
        } else {
          // For filtered conversations
          state.hasMore = !!action.meta.arg.has_more;
          state.totalConversations = action.meta.arg.total_count || state.ids.length;

          // Update cursor for pagination
          const lastConversation = action.payload[action.payload.length - 1];
          state.lastCursor = lastConversation
            ? (lastConversation.latest_delivery_at || lastConversation.latest_message_at || lastConversation.updated_at)
            : null;
        }
      })
      .addCase(fetchFilteredConversations.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.error = action.error.message as string;
        console.error('Error fetching filtered conversations:', state.error);
      })
      // Handle actions from conversations.slice.tsx
      .addCase(fetchConversationsWithMessages.fulfilled, (state, action) => {
        const payload = action.payload as any;

        if (!payload || !Array.isArray(payload.conversations)) {
          // Nothing usable came back – exit early
          return;
        }

        if (!state.isFilterActive) {
          // Check if this is a cursor-based pagination request
          const hasCursor = action.meta.arg.cursor && action.meta.arg.cursor !== null;
          
          if (hasCursor) {
            // For cursor-based pagination, append new conversations
            filteredConversationsAdapter.addMany(state, payload.conversations);
          } else {
            // For initial load or filter changes, replace all conversations
            filteredConversationsAdapter.setAll(state, payload.conversations);
          }

          // Update pagination state 
          state.totalConversations = payload.total_count || state.ids.length;
          state.hasMore = payload.has_more || false;

          // Track the cursor using latest_delivery_at with fallback to latest_message_at
          const lastConversation = payload.conversations[payload.conversations.length - 1];
          state.lastCursor = lastConversation
            ? (lastConversation.latest_delivery_at || lastConversation.latest_message_at || lastConversation.updated_at)
            : null;
        } else {
          state.conversationsUpdatedSinceFilterSet = true;
        }
      })
      .addCase(fetchConversationWithMessages.fulfilled, (state, action) => {
        filteredConversationsAdapter.upsertOne(state, action.payload);
        state.conversationsUpdatedSinceFilterSet = false;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const conversationId = action.payload.conversation_id;
        const message = action.payload.message;
        const existingConversation = state.entities[conversationId];
        if (existingConversation) {
          const messageExists = existingConversation.messages.some((msg) => msg.id === message.id);
          if (!messageExists) {
            // Create a new array with all messages plus the new one
            // This avoids mutating state.entities[conversationId].messages directly
            state.entities[conversationId].messages = [
              ...existingConversation.messages,
              { ...message } // Create a copy of the message to avoid reference issues
            ];
          }
        }
        state.conversationsUpdatedSinceFilterSet = false;
      })
      .addCase(updateConversationStatus.fulfilled, (state, action) => {
        const conversation = action.payload.conversation;
        filteredConversationsAdapter.upsertOne(state, conversation);
        state.conversationsUpdatedSinceFilterSet = false;
      })
      .addCase(fetchPriorityConversations.pending, (state) => {
        state.priorityConversationsLoading = true;
      })
      .addCase(fetchPriorityConversations.fulfilled, (state, action) => {
        state.priorityConversationsLoading = false;
        // Store priority conversations separately
        state.priorityConversations = action.payload.conversations;
        state.hasMorePriority = action.payload.has_more;
        // We don't use upsertMany here anymore - keeps priority separate from adapter state
      })
      .addCase(fetchPriorityConversations.rejected, (state) => {
        state.priorityConversationsLoading = false;
        state.priorityConversations = [];
      })
      .addCase(fetchConversationsFullData.fulfilled, (state, action) => {
        if (!action.payload || !Array.isArray(action.payload.conversations)) {
          return;
        }

        // Update priority conversations with full data if they exist
        const fullDataConversations = action.payload.conversations;
        const updatedPriorityConversations = state.priorityConversations.map(priorityConv => {
          const fullDataConv = fullDataConversations.find(c => c.id === priorityConv.id);
          if (fullDataConv) {
            // Merge full data into priority conversation
            return {
              ...priorityConv,
              ...fullDataConv,
              _hasFullData: true
            };
          }
          return priorityConv;
        });

        state.priorityConversations = updatedPriorityConversations;
      })
      // Handle deletion of conversations
      .addCase(deleteConversations.fulfilled, (state, action) => {
        const { successfulDeletes } = action.payload;
        // Remove deleted conversations from the filtered adapter
        filteredConversationsAdapter.removeMany(state, successfulDeletes);
        // Also remove from priority conversations
        state.priorityConversations = state.priorityConversations.filter(
          conv => !successfulDeletes.includes(conv.id)
        );
      });
  },
});

export const { setFilter } = filteredConversationsSlice.actions;

export const {
  selectById: selectFilteredConversationById,
  selectAll: selectAllFilteredConversations,
} = filteredConversationsAdapter.getSelectors(
  (state: RootState) => state.filteredConversations
);

export const selectIsFilterActive = (state: RootState) =>
  state.filteredConversations.isFilterActive;

export const selectPriorityConversations = (state: RootState) => state.filteredConversations.priorityConversations;
export const selectHasMorePriority = (state: RootState) => state.filteredConversations.hasMorePriority;
export const selectFilteredLastCursor = (state: RootState) => state.filteredConversations.lastCursor;

export default filteredConversationsSlice.reducer;
